import React from 'react';
import {
  Box,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  styled,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CompanyTypeLabelEnum, getAddressText } from '@utils/constants';
import { useAppSelector } from '@store/hooks';
import {
  selectActiveBankAccount,
  selectBankAccounts,
  selectActiveCompany,
  selectLatestProductsStatusInfo,
} from '@features/workspace/store/selectors';
import { LatestProductStart } from '@features/workspace/components/shared/latest-product-start';
import { LatestProductStatus } from '@features/workspace/components/shared/latest-product-status';
import history from '@app/services/history';
import { LayoutType } from '../const';

const useStyles = makeStyles()({
  header: {
    backgroundColor: '#DFF1F7',
    boxShadow: '0px 0px 4px 1px rgba(71, 139, 170, 0.50) inset',
    border: '1px solid #254F6A',
    borderRadius: '10px',
  },
  bankAccountSelectPaper: {
    border: '3px solid red',
  },
});

const StyledSelect = styled(Select)({
  width: '100%',
  backgroundColor: '#EDEEEF',
  borderRadius: '8px',
  '& .MuiInputBase-input': {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '20px',
    padding: '12px 16px',
    color: '#6C6C6C',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: '8px',
  },
  '&.Mui-focused': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#A6A6A6',
    },
  },
});

interface IProps {
  layoutType?: LayoutType;
}

const PageHeader: React.FC<IProps> = ({ layoutType = LayoutType.CUSTOMER }) => {
  const { classes } = useStyles();

  const company = useAppSelector(selectActiveCompany);
  const latestProductsInfo = useAppSelector(selectLatestProductsStatusInfo);
  const bankAccounts = useAppSelector(selectBankAccounts);
  const activeBankAccount = useAppSelector(selectActiveBankAccount);

  const onActiveBankAccountChange = (accNumber: string) => {
    history.push(`/customer/banking/${accNumber}/transactions`);
  };

  return (
    <>
      <Box className={classes.header} display="flex" flexGrow={1} p={2}>
        <Grid container spacing={1.5}>
          {/* Company name, type and address */}
          <Grid item xs={6}>
            <Box display="flex" flexDirection="column" flexGrow={1}>
              <Box alignItems="center" display="flex" gap={1}>
                <Box
                  display="flex"
                  style={{
                    fontWeight: 700,
                    fontSize: '16px',
                    lineHeight: '22px',
                  }}
                >
                  {company?.name}
                </Box>
                <Box
                  display="flex"
                  style={{
                    fontWeight: 700,
                    fontSize: '16px',
                    lineHeight: '22px',
                  }}
                >
                  {company && CompanyTypeLabelEnum[company?.companyType]}
                </Box>
              </Box>
              <Box
                display="flex"
                style={{
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '20px',
                  color: '#5E5E5E',
                }}
              >
                {getAddressText(company?.address)}
              </Box>
            </Box>
          </Grid>
          {/* Customer and documents */}
          {layoutType !== LayoutType.BANKING && (
            <>
              {/* 2nd col info - applications */}
              <Grid item xs={2}>
                <Box display="flex" flexDirection="column" pt={3}>
                  {latestProductsInfo.map((productInfo: any) => (
                    <Box
                      key={`header-product-info-${productInfo?.latestProduct?.item?.id}`}
                      display="flex"
                    >
                      <LatestProductStart productInfo={productInfo} />
                    </Box>
                  ))}
                </Box>
              </Grid>
              {/* 3rd col info - banking submission */}
              {/* TODO: https://www.figma.com/file/iRx0JDsL5noAY1d25KzSco/Lula-Back-office?node-id=1692%3A24681&mode=dev */}
              <Grid item xs={2} />
              {/* 4th col info - status */}
              <Grid item xs={2}>
                <Box display="flex" justifyContent="flex-end" pt={3}>
                  <LatestProductStatus productInfo={latestProductsInfo[0]} />
                </Box>
              </Grid>
            </>
          )}
          {/* Banking */}
          {layoutType === LayoutType.BANKING && (
            <>
              {/* 2nd col */}
              <Grid item xs={3} />
              {/* 3rd col info - ? */}
              <Grid item xs={3} />
            </>
          )}
          {/* 2nd row with account selector */}
          {layoutType === LayoutType.BANKING && (
            <Grid item xs={12}>
              <Box display="flex">
                <Box display="flex" flexBasis="60%">
                  {!!bankAccounts?.length && (
                    <StyledSelect
                      MenuProps={{
                        sx: {
                          '& .MuiMenu-paper': {
                            borderRadius: '8px',
                          },
                        },
                      }}
                      value={activeBankAccount?.accountNumber ?? ''}
                      onChange={(event: SelectChangeEvent<unknown>) => {
                        onActiveBankAccountChange(event.target.value as string);
                      }}
                    >
                      {bankAccounts.map((acc) => (
                        <MenuItem
                          key={`bank-account-selector-${acc.accountNumber}`}
                          value={acc.accountNumber}
                        >
                          {acc.name} - Acc No: {acc.accountNumber} (
                          {acc.statusName})
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  )}
                </Box>
                <Box alignItems="center" display="flex" ml={3}>
                  {activeBankAccount && (
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      style={{
                        fontWeight: 700,
                        fontSize: '14px',
                        lineHeight: '20px',
                        borderRadius: '8px',
                        border: '1px solid #254F6A',
                        background: '#DFF1F7',
                        padding: '6px 16px',
                      }}
                    >
                      Available Balance:&nbsp;
                      {activeBankAccount.currency !== 'ZAR'
                        ? `${activeBankAccount.amount} ${activeBankAccount.currency}`
                        : `R ${activeBankAccount.amount}`}
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Box>
    </>
  );
};

export default PageHeader;
